import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getSubscriptionStatus, syncIAP, SubscriptionStatus } from '../api/astrology';
import { shouldUseIAP, checkIAPSubscription, initializeIAP } from '../services/iap';

interface SubscriptionContextType {
  tier: 'free' | 'premium';
  status: SubscriptionStatus | null;
  loading: boolean;
  isPremium: boolean;
  useIAP: boolean;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType>({
  tier: 'free',
  status: null,
  loading: true,
  isPremium: false,
  useIAP: false,
  refresh: async () => {},
});

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const useIAP = shouldUseIAP();

  const refresh = useCallback(async () => {
    if (!user?.userId) return;

    try {
      if (useIAP) {
        // On iOS: check RevenueCat first (with timeout), then fall back to server
        try {
          const timeout = new Promise<{ isPremium: false }>((resolve) =>
            setTimeout(() => resolve({ isPremium: false }), 10000)
          );
          const iapStatus = await Promise.race([checkIAPSubscription(user.userId), timeout]);
          if (iapStatus.isPremium) {
            // Self-heal: make sure the backend also knows (fixes purchases
            // made before /iap/sync existed; server verifies w/ RevenueCat)
            syncIAP(user.userId).catch((e) => console.warn('IAP sync failed:', e));
            setStatus({
              tier: 'premium',
              status: 'active',
              plan: (iapStatus as any).productId,
            });
            return;
          }
        } catch (e) {
          console.warn('IAP check failed, falling back to server:', e);
        }
      }

      // Web or IAP didn't find premium: check server (Stripe)
      const serverStatus = await getSubscriptionStatus(user.userId);
      setStatus(serverStatus);
    } catch {
      setStatus({ tier: 'free', status: 'none' });
    } finally {
      setLoading(false);
    }
  }, [user?.userId, useIAP]);

  // Initialize IAP on iOS
  useEffect(() => {
    if (useIAP && user?.userId) {
      initializeIAP(user.userId);
    }
  }, [useIAP, user?.userId]);

  useEffect(() => {
    if (isAuthenticated && user?.userId) {
      refresh();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user?.userId, refresh]);

  // Check URL for subscription=success and refresh (web Stripe flow)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscription') === 'success') {
      setTimeout(refresh, 2000);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [refresh]);

  const tier = status?.tier || 'free';
  const isPremium = tier === 'premium';

  return (
    <SubscriptionContext.Provider value={{ tier, status, loading, isPremium, useIAP, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
};
